import React, { useState } from "react";
import Sidebar from "../../../components/sidebar";
import { useNavigate, useOutletContext } from "react-router-dom";
import "./style.scss";
import { useFormik } from "formik";
import * as Yup from "yup";
import { emailRegex, nameRegex } from "../../../constants/utlis";
import { LANG } from "../../../constants/language";
import clsx from "clsx";
import ErrorText from "../../../core/components/error-text";
import { all_routes } from "../../router/all_routes";
import { useDispatch, useSelector } from "react-redux";
import { sendUserInvite } from "../../../core/data/redux/manageUser/_manageUserAction";

// Define validation schema using Yup
const validationSchema = Yup.object({
  // name: Yup.string().matches(nameRegex, 'The name can only include letters, spaces, hyphens, or apostrophes.').required("Name is required"),
  name: Yup.string()
    .required("Name is required")
    .trim()
    .test(
      "no-whitespace-only",
      "Name cannot be empty or just whitespace",
      (value) => value !== undefined && value.trim().length > 0
    )
    .min(2, "Name must be at least 2 characters")
    .matches(nameRegex, "Name can only contain letters, spaces, hyphens, apostrophes, and periods"),
  // email: Yup.string().email(LANG.PLEASE_ADD_VALID_EMAIL).required("Email is required"),
  email: Yup.string()
    .required(LANG.EMAIL_IS_REQUIRED)
    .matches(emailRegex, LANG.PLEASE_ADD_VALID_EMAIL),
  role: Yup.string()
    .oneOf(["spouse", "tax_professional"], "Please select a valid user type")
    .required("Role is required"),
});

// Interface for form values
interface FormValues {
  name: string;
  email: string;
  role: string;
  uuid?: string;
}

const AddMember: React.FC = () => {
  const { isSidebarOpen, onToggleSidebar } = useOutletContext<{
    isSidebarOpen: boolean;
    onToggleSidebar: () => void;
  }>();

  const route = all_routes;
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const userList = useSelector((state: any) => state?.manageUser?.userList);

  const isSpouseAccepted = userList?.some(
    (user: any) => user?.role?.toLowerCase() === "spouse"
    // && user?.status?.toLowerCase() === "accept"
  );
  const isTaxProAccepted = userList?.some(
    (user: any) => user?.role?.toLowerCase() === "tax_professional"
    //  && user?.status?.toLowerCase() === "accept"
  );

  // Check if all tax professionals are archived
  const allTaxProsArchived = userList
    ?.filter((user: any) => (user?.role?.toLowerCase() === "tax professional") || (user?.role?.toLowerCase() === "tax_professional"))
    ?.every((user: any) => user?.archieve === true);

  // Formik setup
  const formik = useFormik<FormValues>({
    initialValues: {
      name: "",
      email: "",
      role: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      try {
        await dispatch(sendUserInvite(values));
        setSubmitting(false);
        setLoading(false);
        navigate(route.manageUser);
      } catch (error) {
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  return (
    <div className="layout">
      {/* Sidebar */}
      <Sidebar isOpen={isSidebarOpen} />

      {/* Main Content */}
      <main className="content">
        <div className="profileSettings add-member">
          <h2>Invite User</h2>

          <form onSubmit={formik.handleSubmit}>
            <div className="row mb-3 mt-4">
              <div className="col-md-6 mb-3 mb-md-0">
                <label htmlFor="name" className="form-label">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  placeholder="Enter Name"
                  {...formik.getFieldProps("name")}
                  className={clsx("form-control commonInput  bg-light", {
                    "border border-danger":
                      formik.touched.name && formik.errors.name,
                  })}
                  onBlur={(e) => {
                    const trimmedValue = e.target.value.trim();
                    formik.setFieldValue("name", trimmedValue);
                    formik.handleBlur(e); // keep touched state
                  }}
                />
                <ErrorText
                  show={formik.touched.name && !!formik.errors.name}
                  message={formik.errors.name}
                />
              </div>

              <div className="col-md-6">
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  placeholder="Enter your email"
                  {...formik.getFieldProps("email")}
                  className={clsx("form-control commonInput bg-light", {
                    "border border-danger":
                      formik.touched.email && formik.errors.email,
                  })}
                />
                <ErrorText
                  show={formik.touched.email && !!formik.errors.email}
                  message={formik.errors.email}
                />
              </div>
            </div>

            <div className="user-type-box mb-4 p-3 rounded bg-light form-control commonInput">
              <label className="form-label">Choose User Type</label>
              <div className="btn-radio d-flex">

                <div className={`me-3 ${isSpouseAccepted ? "disabled" : ""}`}>
                  <input
                    type="radio"
                    id="spouse"
                    name="role"
                    value="spouse"
                    checked={formik.values.role === "spouse"}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isSpouseAccepted}
                  />
                  <label htmlFor="spouse" className="radio-label">
                    Spouse
                  </label>
                </div>

                <div className={`me-3 ${!allTaxProsArchived ? "disabled" : ""}`}>
                  <input
                    type="radio"
                    id="taxPro"
                    name="role"
                    value="tax_professional"
                    checked={formik.values.role === "tax_professional"}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!allTaxProsArchived}
                  />
                  <label htmlFor="taxPro" className="radio-label">
                    Tax Professional
                  </label>
                </div>
              </div>
              <ErrorText
                show={formik.touched.role && !!formik.errors.role}
                message={formik.errors.role}
              />
            </div>

            <div className="text-end">
              <button
                type="submit"
                className="btn primaryButton  btn-primary d-inline-flex justify-content-center align-items-center  btn-block"
                disabled={loading || formik.isSubmitting}
              >
                {loading ? "Sending..." : "Send Invite"}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AddMember;
